import { SectionSeatStatus } from "../../../models/Event";
import { SeatGeom } from "./seatGeometry";

/**
 * Pure port of the seat-appearance branches previously implemented as SVG in
 * SectionSeatComponent. Given a seat's geometry and the current reactive state
 * (cart, hold group, statuses), computes everything the canvas needs to draw
 * the seat and decide how the pointer may interact with it.
 */

/**
 * - "full": clickable and hoverable (available seats).
 * - "hover": popover on hover but clicks are swallowed (unavailable seats).
 * - "none": inert; pointer events pass nothing through (hold-group seats,
 *   box-office-only seats without a message, require-entire-section seats).
 */
export type SeatInteractivity = "full" | "hover" | "none";

export type SeatVisual = {
    fill: string;
    stroke: string;
    /** Radius to draw, in seatmap user units (includes the in-cart +1 bump). */
    radius: number;
    check: boolean;
    accessible: boolean;
    obstructed: boolean;
    interactivity: SeatInteractivity;
    inCart: boolean;
    inHoldGroup: boolean;
};

export type PaintContext = {
    cartSeatIds: Set<number>;
    packageSeatIds: Set<number>;
    holdGroupSeatIds: Set<number>;
    statusById: Map<number, SectionSeatStatus>;
    boxoffice: boolean;
};

const FILL_ALPHA = 0.7;

export function getSeatFillAlpha(): number {
    return FILL_ALPHA;
}

/** Returns null for inactive seats (legacy rendered nothing for them). */
export function computeSeatVisual(
    g: SeatGeom,
    ctx: PaintContext,
): SeatVisual | null {
    const seat = g.seat;
    if (!seat.isActive) return null;

    const inCart =
        ctx.cartSeatIds.has(seat.id) || ctx.packageSeatIds.has(seat.id);
    const inHoldGroup = ctx.holdGroupSeatIds.has(seat.id);
    const status = ctx.statusById.get(seat.id);

    if (inHoldGroup) {
        return {
            fill: "yellow",
            stroke: "black",
            radius: g.r,
            check: false,
            accessible: false,
            obstructed: false,
            interactivity: "none",
            inCart,
            inHoldGroup,
        };
    }

    const priceGroup = g.priceGroup;
    const unavailable =
        (seat.isBoxofficeOnly && !ctx.boxoffice) ||
        !priceGroup ||
        !(
            (priceGroup.type === "seat" && priceGroup.priceGroup) ||
            priceGroup.type === "package"
        ) ||
        !status ||
        (!(
            status.seatStatus &&
            status.packageStatus &&
            status.seatHoldGroupStatus &&
            status.packageHoldGroupStatus
        ) &&
            !inCart);

    if (unavailable) {
        let unavailableFill = "grey";
        if (
            ctx.boxoffice &&
            status &&
            status.seatStatus &&
            status.packageStatus
        ) {
            if (!status.seatHoldGroupStatus) {
                unavailableFill = "orange";
            } else if (!status.packageHoldGroupStatus) {
                unavailableFill = "purple";
            }
        }
        return {
            fill: unavailableFill,
            stroke: "black",
            radius: g.r,
            check: false,
            accessible: false,
            obstructed: false,
            interactivity:
                seat.isBoxofficeOnly && !g.message ? "none" : "hover",
            inCart,
            inHoldGroup,
        };
    }

    const isSeat = priceGroup.type === "seat";
    const color = isSeat ? priceGroup.priceGroup!.color : "green";

    return {
        fill: color,
        stroke: color,
        radius: g.r + (inCart ? 1 : 0),
        check: inCart,
        accessible: !inCart && seat.isAccessible,
        obstructed: !inCart && seat.isObstructed,
        interactivity: g.requireEntireSection ? "none" : "full",
        inCart,
        inHoldGroup,
    };
}
